import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { Photo, SearchFilters, GalleryState } from '../types';
import { apiClient } from '../services/api';

// Action types for the reducer
type GalleryAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_PHOTOS'; payload: Photo[] }
  | { type: 'APPEND_PHOTOS'; payload: Photo[] }
  | { type: 'SET_PAGINATION'; payload: { currentPage: number; totalPages: number } }
  | { type: 'SET_SEARCH_FILTERS'; payload: SearchFilters }
  | { type: 'SET_SELECTED_PHOTO'; payload: Photo | null }
  | { type: 'SET_LOADING_MORE'; payload: boolean }
  | { type: 'RESET_GALLERY' };

// Initial state
const initialState: GalleryState = {
  photos: [],
  currentPage: 1,
  totalPages: 1,
  loading: false,
  error: null,
  selectedPhoto: null,
  loadingMore: false,
  searchFilters: {
    startDate: null,
    endDate: null,
  },
};

// Reducer function
const galleryReducer = (state: GalleryState, action: GalleryAction): GalleryState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_PHOTOS':
      return { ...state, photos: action.payload };
    case 'APPEND_PHOTOS':
      return { ...state, photos: [...state.photos, ...action.payload] };
    case 'SET_PAGINATION':
      return { 
        ...state, 
        currentPage: action.payload.currentPage,
        totalPages: action.payload.totalPages 
      };
    case 'SET_SEARCH_FILTERS':
      return { ...state, searchFilters: action.payload };
    case 'SET_SELECTED_PHOTO':
      return { ...state, selectedPhoto: action.payload };
    case 'SET_LOADING_MORE':
      return { ...state, loadingMore: action.payload };
    case 'RESET_GALLERY':
      return { ...initialState };
    default:
      return state;
  }
};

// Context interface
interface GalleryContextType {
  state: GalleryState;
  actions: {
    loadPhotos: (page?: number, filters?: SearchFilters, append?: boolean) => Promise<void>;
    searchPhotos: (filters: SearchFilters) => Promise<void>;
    clearSearch: () => Promise<void>;
    loadMorePhotos: () => Promise<void>;
    selectPhoto: (photo: Photo | null) => void;
    retryLoadPhotos: () => Promise<void>;
  };
}

// Create context
const GalleryContext = createContext<GalleryContextType | undefined>(undefined);

// Provider component
interface GalleryProviderProps {
  children: React.ReactNode;
}

export const GalleryProvider: React.FC<GalleryProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(galleryReducer, { ...initialState, loadingMore: false });

  // Load photos with error handling and retry logic
  const loadPhotos = useCallback(async (
    page = 1, 
    filters?: SearchFilters, 
    append = false
  ) => {
    try {
      if (append) {
        dispatch({ type: 'SET_LOADING_MORE', payload: true });
      } else {
        dispatch({ type: 'SET_LOADING', payload: true });
      }
      dispatch({ type: 'SET_ERROR', payload: null });

      const params: any = { page, limit: 20 };
      
      if (filters?.startDate) {
        params.startDate = filters.startDate.toISOString().split('T')[0];
      }
      if (filters?.endDate) {
        params.endDate = filters.endDate.toISOString().split('T')[0];
      }

      const response = await apiClient.getPhotos(params);
      
      if (append) {
        dispatch({ type: 'APPEND_PHOTOS', payload: response.items });
      } else {
        dispatch({ type: 'SET_PHOTOS', payload: response.items });
      }
      
      dispatch({ 
        type: 'SET_PAGINATION', 
        payload: { 
          currentPage: response.currentPage, 
          totalPages: response.totalPages 
        } 
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load photos';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
      dispatch({ type: 'SET_LOADING_MORE', payload: false });
    }
  }, []);

  // Search photos with filters
  const searchPhotos = useCallback(async (filters: SearchFilters) => {
    dispatch({ type: 'SET_SEARCH_FILTERS', payload: filters });
    await loadPhotos(1, filters);
  }, [loadPhotos]);

  // Clear search and reload all photos
  const clearSearch = useCallback(async () => {
    const emptyFilters: SearchFilters = { startDate: null, endDate: null };
    dispatch({ type: 'SET_SEARCH_FILTERS', payload: emptyFilters });
    await loadPhotos(1, emptyFilters);
  }, [loadPhotos]);

  // Load more photos for pagination
  const loadMorePhotos = useCallback(async () => {
    if (state.currentPage < state.totalPages && !state.loadingMore) {
      await loadPhotos(state.currentPage + 1, state.searchFilters, true);
    }
  }, [state.currentPage, state.totalPages, state.loadingMore, state.searchFilters, loadPhotos]);

  // Select photo for viewer
  const selectPhoto = useCallback((photo: Photo | null) => {
    dispatch({ type: 'SET_SELECTED_PHOTO', payload: photo });
  }, []);

  // Retry loading photos (useful for error recovery)
  const retryLoadPhotos = useCallback(async () => {
    await loadPhotos(state.currentPage, state.searchFilters);
  }, [state.currentPage, state.searchFilters, loadPhotos]);

  // Initial load on mount
  useEffect(() => {
    loadPhotos(1);
  }, [loadPhotos]);

  const contextValue: GalleryContextType = {
    state,
    actions: {
      loadPhotos,
      searchPhotos,
      clearSearch,
      loadMorePhotos,
      selectPhoto,
      retryLoadPhotos,
    },
  };

  return (
    <GalleryContext.Provider value={contextValue}>
      {children}
    </GalleryContext.Provider>
  );
};

// Custom hook to use the gallery context
export const useGallery = (): GalleryContextType => {
  const context = useContext(GalleryContext);
  if (context === undefined) {
    throw new Error('useGallery must be used within a GalleryProvider');
  }
  return context;
};